/**
 * TK-46 §1 (lee 25/10: "ẩn / hiện theo ngữ cảnh") — MỘT khối "Điều khiển độ sáng" ở Vận hành, thay các khối dim rời (khung tuyến ·
 *   khung tủ · ô đèn). Hàng phạm vi CHỈ hiện phạm vi dùng được cho đối tượng đang chọn (`phamViHien`):
 *     tuyến ⇒ Tuyến · Nhóm · tủ ⇒ Tủ · Nhóm · Tuyến · đèn ⇒ Đèn · Tủ · Nhóm. Thân dùng chung (`DimCap`).
 *   · Nhóm: ô NHẬP SỐ 0–254 (như ô Nhóm TK-45 §3, tên nhóm mờ bên cạnh), phạm vi lệnh theo cấp đang chọn (tủ ⇒ trong tủ, không ⇒ cả
 *     tuyến); đích = đèn có số nhóm thiết bị N VÀ nhận lệnh (`dichNhom`); đèn chưa rõ nhóm không tính ("chưa rõ nhóm k đèn").
 *   · Một dòng "x / y đèn nhận lệnh"; không đèn nào nhận ⇒ thân thu còn một dòng lý do.
 *   · Không có quyền điều khiển (VIEWER) ⇒ một dòng, không bày khối.
 *   · TK-48 (bản v06): là THÂN thẻ "Độ sáng" của khung điều khiển nổi (`KhungDieuKhien`); thẻ Độ sáng / Nguồn AC, tên đối tượng và
 *     chip loại nằm ở khung. Phạm vi dạng thanh phân đoạn; chỉ một phạm vi ⇒ ẩn thanh.
 */
import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../lib/auth';
import type { TreeRoute } from '../../lib/types';
import { chuLyDo, khoaLenhDen, tenDen, type LyDoKhongNhan } from '../../lib/nemaNhan';
import { NHAN_PHAM_VI, dangBao, dichNhom, lyDoDau, phamViHien, type PhamVi } from '../../lib/dieuKhienDoSang';
import { boSo0, docNhom } from '../../lib/doThiDim';
import type { DenBanDo } from '../nema/LopDenNema';
import { DimCap, type DichDim } from './DimCap';
import { khoaDen } from './BangDenTuyen';

/**
 * TK-48: trang có bày thẻ "Độ sáng" không — cùng cổng với thân khối ở dưới (VIEWER / không cờ điều khiển tuyến ⇒ không có thẻ).
 *   Đặt ở đây (thành phần) để TRANG không tự đọc quyền ngoài `Khoa` (phép verify "mọi trang đọc quyền đi qua Khoa").
 */
export function useCoTheDoSang(tuyenId: string | null): boolean {
  const qd = useAuth().coCoTuyen('canControl', tuyenId);
  return qd === 'cho-qua' || qd === 'chua-biet';
}

export function KhoiDieuKhien({ r, tuChon, den, denCuaTuyen, nhom, tuNema }: {
  r: TreeRoute;
  /** tủ đang chọn (`?tu=`) — đèn đang chọn thì tủ của đèn */
  tuChon: string | null;
  /** đèn đang chọn (ô đèn mở) */
  den: DenBanDo | null;
  /** đèn THUỘC HỆ THỐNG của tuyến (máy chủ tính nhanLenh / lyDo / dim) */
  denCuaTuyen: DenBanDo[];
  /** nhóm đã đặt tên trong CSDL (số nhóm ⇒ tên mờ) */
  nhom: { so: number | null; name: string }[];
  /** lyDoTraVe theo tủ (null = trả về lịch đi được) */
  tuNema: { id: string; name: string; lyDoTraVe?: LyDoKhongNhan | null }[];
}) {
  const qd = useAuth().coCoTuyen('canControl', r.id);
  const tuId = den?.cabinetId ?? tuChon;
  const pv = phamViHien({ tuyen: r.id, tu: tuId, den: den ? khoaDen(den) : null });
  const khoaChon = `${r.id}|${tuId ?? ''}|${den ? khoaDen(den) : ''}`;
  const [pham, setPham] = useState<PhamVi | null>(pv.macDinh);
  useEffect(() => { setPham(pv.macDinh); }, [khoaChon]);   // đổi đối tượng ⇒ về phạm vi mặc định
  const [chuNhom, setChuNhom] = useState('');
  const soNhom = docNhom(chuNhom);

  const tinh = useMemo(() => {
    const cuaTu = denCuaTuyen.filter((d) => d.cabinetId === tuId);
    const tenTu = r.cabinets.find((c) => c.id === tuId)?.name ?? 'Tủ';
    if (pham === 'den' && den) {
      const k = khoaLenhDen(den);
      return { ten: tenDen(den), dich: { loai: 'den', cabinetId: den.cabinetId, addr: den.addr } as DichDim, nhan: den.nhanLenh ? 1 : 0, tong: 1,
        muc: dangBao(den, []), khoaVi: k.dim, khoaTraVe: k.traVe, chuaRo: 0, tuLienQuan: [den.cabinetId] };
    }
    if (pham === 'nhom') {
      const nen = tuId ? cuaTu : denCuaTuyen;
      if (soNhom === null) return { ten: 'Nhóm', dich: null, nhan: 0, tong: 0, muc: null, khoaVi: 'Nhập số nhóm 0–254', khoaTraVe: 'Nhập số nhóm 0–254', chuaRo: 0, tuLienQuan: [] };
      const d = dichNhom(nen, soNhom);
      const tuLQ = [...new Set(d.trongNhom.map((x) => x.cabinetId))];
      return {
        ten: `Nhóm ${soNhom}${tuId ? ` · ${tenTu}` : ''}`, dich: { loai: 'nhom', so: soNhom, tuId: tuId ?? null, tuyenId: r.id } as DichDim,
        nhan: d.nhan.length, tong: d.trongNhom.length, muc: dangBao(null, d.trongNhom), chuaRo: d.chuaRo, tuLienQuan: tuLQ,
        khoaVi: d.trongNhom.length === 0 ? `Nhóm ${soNhom} không có đèn${tuId ? ' trong tủ' : ' trong tuyến'}`
          : d.nhan.length === 0 ? `${chuLyDo(lyDoDau(d.trongNhom)) ?? 'Không nhận lệnh'} — không đèn nào nhận lệnh` : null,
        khoaTraVe: null as string | null,
      };
    }
    const ds = pham === 'tu' ? cuaTu : denCuaTuyen;
    const nhan = ds.filter((x) => x.nhanLenh === true).length;
    return {
      ten: pham === 'tu' ? tenTu : r.tenDayDu, dich: (pham === 'tu' ? { loai: 'tu', id: tuId! } : { loai: 'tuyen', id: r.id }) as DichDim,
      nhan, tong: ds.length, muc: dangBao(null, ds), chuaRo: 0, tuLienQuan: [...new Set(ds.map((x) => x.cabinetId))],
      khoaVi: ds.length === 0 ? 'Chưa có đèn' : nhan === 0 ? `${chuLyDo(lyDoDau(ds)) ?? 'Không nhận lệnh'} — không đèn nào nhận lệnh` : null,
      khoaTraVe: null as string | null,
    };
  }, [pham, den, tuId, denCuaTuyen, soNhom, r]);
  /* Trả về lịch: tủ trực tuyến + mesh bật (máy chủ tính `lyDoTraVe` theo tủ) — khoá khi KHÔNG tủ nào liên quan nhận được */
  const khoaTraVe = tinh.khoaTraVe ?? (() => {
    if (pham === 'den') return null;
    const ly = tinh.tuLienQuan.map((id) => tuNema.find((t) => t.id === id)?.lyDoTraVe ?? null);
    return ly.length > 0 && ly.every((x) => x) ? chuLyDo(ly[0]) : null;
  })();

  if (!pham) return null;
  /* VIEWER / không có cờ điều khiển tuyến này ⇒ KHÔNG bày khối (phiếu: "không thấy khối"; câu "không có quyền" chỉ dựng ở Khoa.tsx) */
  if (qd !== 'cho-qua' && qd !== 'chua-biet') return null;
  const tenNhom = soNhom !== null ? nhom.find((g) => g.so === soNhom)?.name ?? null : null;
  const loiNhom = pham === 'nhom' && chuNhom !== '' && soNhom === null;
  const cauNhan = <>{pham === 'nhom' && soNhom !== null ? `Nhóm ${soNhom} · ` : ''}{tinh.nhan} / {tinh.tong} đèn nhận lệnh</>;
  return (
    <section className="op-khoi-dim" data-o="khoi-dieu-khien" data-pham-vi={pham}>
      {pv.ds.length > 1 && (
        <div className="op-pham-vi" role="tablist" aria-label="Phạm vi" data-o="pham-vi">
          {pv.ds.map((p) => (
            <button key={p} type="button" role="tab" aria-selected={p === pham} onClick={() => setPham(p)} data-pham={p}>{NHAN_PHAM_VI[p]}</button>
          ))}
        </div>
      )}
      {pham === 'nhom' && (
        <div className="flex flex-wrap items-center gap-2" data-o="chon-nhom">
          <label className="flex items-center gap-2 text-sm text-chu-2">Nhóm
            <input type="text" inputMode="numeric" maxLength={3} value={chuNhom} aria-label="Số nhóm" aria-invalid={loiNhom} data-o="so-nhom"
              onChange={(e) => setChuNhom(boSo0(e.target.value.replace(/\D/g, '')))} placeholder="0–254"
              className={`min-h-[36px] w-[4.5rem] rounded-lg border bg-nen-lom px-2 text-right text-sm tabular-nums text-chu-1 ${loiNhom ? 'border-nguy' : 'border-vien'}`} />
          </label>
          {loiNhom && <span className="text-xs text-nguy" data-o="loi-so-nhom">0–254</span>}
          {tenNhom && <span className="max-w-[12rem] truncate text-xs text-chu-3" title={tenNhom} data-o="ten-nhom">{tenNhom}</span>}
          {tinh.chuaRo > 0 && <span className="text-xs text-chu-3" data-o="chua-ro-nhom">chưa rõ nhóm {tinh.chuaRo} đèn</span>}
        </div>
      )}
      {!tinh.khoaVi && <p className="op-nhan-lenh" data-o="so-nhan-lenh">{cauNhan}</p>}
      {tinh.dich
        ? <DimCap key={`${pham}|${khoaChon}|${soNhom ?? ''}`} dich={tinh.dich} ten={tinh.ten} qd={qd} mucDau={tinh.muc} khoaVi={tinh.khoaVi} khoaTraVe={khoaTraVe}
            nhanDim={cauNhan} nhanTraVe={<>{tinh.tong} đèn</>} />
        : <span className="text-sm text-chu-3" data-o="dim-khoa-vi">{tinh.khoaVi}</span>}
    </section>
  );
}
